import { Component, computed, effect, ElementRef, inject, input, model, output, QueryList, signal, viewChild, viewChildren } from '@angular/core';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { SelectedAttributeService } from '../../../services/selected-attribute.service';
import { SkuMapService } from 'src/app/services/sku-map/sku-map.service';
import { assert } from 'src/app/utils/assert.util';
import { SizesService } from 'src/app/services/sizes/sizes.service';
import { ConfiguredItemService } from 'src/app/services/configured-item/configured-item.service';
import { formatFingerSizeForB2BSKU } from 'src/app/utils/configured-item.util';
import { Navigation2Service } from '../../../services/navigation2.service';
import { GoogleAnalyticsService } from 'src/app/services/google-analytics/google-analytics.service';
import { InvalidSkuDialogComponent } from '../../invalid-sku.dialog/invalid-sku.dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { DispatcherService } from 'src/app/services/dispatcher/dispatcher.service';
import { B2bSearchItemProviderService } from 'src/app/providers/b2b-search-item/b2b-search-item.provider.service';
import { B2BSearchItem } from 'src/app/interfaces/b2b-search-item';
import { environment } from 'src/environments/environment';
import { interval, Subscription } from 'rxjs';
import { CheckoutService } from '../../../services/checkout.service';

@Component({
  selector: 'app-item-search',
  templateUrl: './item-search.component.html',
  styleUrl: './item-search.component.scss'
})
export class ItemSearchComponent {
  private checkoutService = inject(CheckoutService);
  private dispatcherService = inject(DispatcherService);
  private b2bItemSearchProvider = inject(B2bSearchItemProviderService);
  private loadingService = inject(LoadingService);
  private selectedAttributeService = inject(SelectedAttributeService);
  private skuMapService = inject(SkuMapService);
  private sizesService = inject(SizesService);
  private configuredItemService = inject(ConfiguredItemService);
  private navigationService = inject(Navigation2Service);
  private googleAnalyticsService = inject(GoogleAnalyticsService);
  private dialog = inject(MatDialog);

  skuToShow = model<string>('');
  closeSearch = output();
  isRetailerOnlyMode = computed(() => this.checkoutService.isRetailerOnly());
  searchResults = signal<B2BSearchItem[]>([]);
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');
  b2bSearchURLBase = signal(`${environment.b2bURL}${environment.b2bSearchPath}`);
  pressedNavKey = signal<string | null>(null);
  selectedIndex = signal(0);
  searchText = signal(this.skuToShow());
  searchResultsContainer = viewChild<ElementRef<HTMLElement>>('searchResultsContainer');
  searchResultElementRefs = viewChildren<QueryList<ElementRef<HTMLButtonElement>>, ElementRef<HTMLElement>>('searchResult', { read: ElementRef });
  indexChangesSubscription: Subscription | null = null;
  keyDownCheckSubscription: Subscription | null = null;
  searchTextUpdateSubscription?: Subscription;
  changePollSubscription?: Subscription;

  constructor() {
    effect(() => {
      this.searchInput()?.nativeElement.focus();
    });

    effect(async () => {
      const value = this.searchText();
      if (value && value.length > 0) {
        await this.dispatcherService.getQueue('itemSearch').run(async () => {
          const results = await this.b2bItemSearchProvider.get(value);
          if (results.length === 0) {
            this.searchResults.set([
              {
                itemNumberOrStyle: value,
                itemDescription: null,
                fingerSize: null,
                canCustomize: true
              }
            ]);
          } else {
            this.searchResults.set(results);
          }
        });
      }
    }, { allowSignalWrites: true });

    effect(() => {
      const selectedSearchResultElement = this.searchResultElementRefs()[this.selectedIndex()]?.nativeElement;
      const searchResultsContainer = this.searchResultsContainer()?.nativeElement;
      if (searchResultsContainer && selectedSearchResultElement) {
        const selectedResultRect = selectedSearchResultElement?.getBoundingClientRect();
        const containerRect = this.searchResultsContainer()?.nativeElement?.getBoundingClientRect();

        if (selectedResultRect && containerRect) {
          const selectedResultRelativeTop = selectedResultRect.y - containerRect.y + searchResultsContainer.scrollTop;
          const selectedResultRelativeBottom = selectedResultRect.height + selectedResultRelativeTop;
          const scrollTop = searchResultsContainer.scrollTop;
          const scrollBottom = scrollTop + containerRect.height;

          if (Math.round(selectedResultRelativeTop) < scrollTop) {
            this.searchResultsContainer()?.nativeElement.scrollTo(0, Math.round(selectedResultRelativeTop));
          } else if (Math.round(selectedResultRelativeBottom) > scrollBottom) {
            this.searchResultsContainer()?.nativeElement.scrollTo(0, scrollTop + Math.round(selectedResultRelativeBottom - scrollBottom));
          }
        }
      }
    });
  }

  async inputModelChange(value: string) {
    this.skuToShow.set(value.trim());
    if (value.length > 2) {
      if (!this.searchTextUpdateSubscription) {
        this.startSearchTextUpdates();
      }
      this.stopChangePollSubscription();
      this.changePollSubscription = interval(100).subscribe(() => {
        this.stopSearchTextUpdates();
        this.stopChangePollSubscription();
        this.searchText.set(this.skuToShow());
      });
    } else {
      this.searchText.set("");
      this.searchResults.set([]);
    }
  }

  inputChange() {
    this.searchSKU(this.skuToShow().trim());
  }

  inputKeyPress(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      this.dispatcherService.getQueue('itemSearch').run(async () => {
        const searchResult = this.searchResults()[this.selectedIndex()];
        if (searchResult) {
          this.resultSelected(searchResult);
        }
      });
    }
  }

  resultSelected(searchResult: B2BSearchItem) {
    if (searchResult.canCustomize) {
      this.searchSKU(searchResult.itemNumberOrStyle.trim());
    } else {
      window.open(`${this.b2bSearchURLBase()}${searchResult.itemNumberOrStyle.trim()}`, '_blank');
    }

    this.closeSearch.emit();
  }

  inputKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.closeSearch.emit();
    } else if (this.searchResults().length > 1 && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      this.pressedNavKey.set(event.key);
      if (event.key === 'ArrowUp') {
        this.changeIndex(-1);
      } else if (event.key === 'ArrowDown') {
        this.changeIndex(1);
      }

      this.keyDownCheckSubscription?.unsubscribe();
      this.keyDownCheckSubscription = interval(500).subscribe(() => {
        if (this.pressedNavKey() === 'ArrowUp') {
          this.startIndexChanges(-1);
        } else if (this.pressedNavKey() === 'ArrowDown') {
          this.startIndexChanges(1);
        }
        this.keyDownCheckSubscription?.unsubscribe();
        this.keyDownCheckSubscription = null;
      });
      event.preventDefault();
    } else if (event.key !== 'Enter') {
      this.selectedIndex.set(0);
    }
  }

  startSearchTextUpdates() {
    this.searchTextUpdateSubscription = interval(100).subscribe(() => {
      if (this.skuToShow() !== this.searchText()) {
        this.searchText.set(this.skuToShow());
      }
    });
  }

  stopSearchTextUpdates() {
    this.searchTextUpdateSubscription?.unsubscribe();
    this.searchTextUpdateSubscription = undefined;
  }

  stopChangePollSubscription() {
    this.changePollSubscription?.unsubscribe();
    this.changePollSubscription = undefined;
  }

  inputKeyUp(event: KeyboardEvent) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      this.pressedNavKey.set(null);
      this.keyDownCheckSubscription?.unsubscribe();
      this.keyDownCheckSubscription = null;
      this.stopIndexChanges();
    }
  }

  startIndexChanges(step: number) {
    if (!this.indexChangesSubscription) {
      this.indexChangesSubscription = interval(50).subscribe(this.changeIndex.bind(this, step));
    }
  }

  changeIndex(step: number) {
    this.selectedIndex.update((currentIndex) => {
      const nextIndex = currentIndex + step;
      if (nextIndex < 0) {
        return this.searchResults().length - 1;
      }

      if (nextIndex >= this.searchResults().length) {
        return 0;
      }

      return nextIndex;
    });
  }

  stopIndexChanges() {
    this.indexChangesSubscription?.unsubscribe();
    this.indexChangesSubscription = null;
  }

  async searchSKU(skuOrStyleNumber: string) {
    this.triggerGAEvent('search', 'item_number_search', skuOrStyleNumber);
    this.loadingService.load('topbar:searchSKU');
    const formattedSKUOrStyleNumber = skuOrStyleNumber.toUpperCase();
    try {
      if (!formattedSKUOrStyleNumber.match(/\D/)) {
        this.selectedAttributeService.addConfiguredItemToUndoList(null);
        const mapEntry = await this.skuMapService.getWithShortCode(+formattedSKUOrStyleNumber);
        assert(mapEntry, 'Could not find item with short code');
        await this.selectedAttributeService.setConfiguredItemFromStyle(mapEntry.ringBuilderSKU, mapEntry.fingerSize, undefined, (b2bData) => {
          b2bData.selectedHaloStyleNumber = undefined;
          b2bData.haloStyle = mapEntry.halosku ?? undefined;
          const size = this.sizesService.values().find(s => s.value === mapEntry.fingerSize);
          assert(size, 'Size was null');
          this.selectedAttributeService.pricingObject.set({ style: mapEntry.halosku ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem()), size });
        });
      } else if (this.configuredItemService.isCustomStyle(formattedSKUOrStyleNumber)) {
        this.selectedAttributeService.addConfiguredItemToUndoList(null);
        const mapEntry = await this.skuMapService.getReverse(formattedSKUOrStyleNumber);
        await this.selectedAttributeService.setConfiguredItemFromStyle(formattedSKUOrStyleNumber, undefined, undefined, (b2bData) => {
          b2bData.selectedHaloStyleNumber = undefined;
          b2bData.haloStyle = mapEntry[0]?.halosku;
          const size = this.selectedAttributeService.configuredItem().size;
          assert(size, 'Size was null');
          this.selectedAttributeService.pricingObject.set({ style: mapEntry[0]?.halosku ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem()), size });
        });
      } else if (this.configuredItemService.isCustomSKU(formattedSKUOrStyleNumber)) {
        const size = formattedSKUOrStyleNumber.split('-')?.slice(-1)[0];
        const sizeObject = this.sizesService.values().find(s => formatFingerSizeForB2BSKU(s.value) === size);
        assert(sizeObject, 'Could not match size from sku');
        const mapEntry = await this.skuMapService.getReverse(formattedSKUOrStyleNumber.split('-')?.slice(0, -1)?.join('-'), sizeObject.value);
        this.selectedAttributeService.addConfiguredItemToUndoList(null);
        await this.selectedAttributeService.setConfiguredItemFromSKU(formattedSKUOrStyleNumber, (b2bData) => {
          b2bData.selectedHaloStyleNumber = undefined;
          b2bData.haloStyle = mapEntry[0]?.halosku;
          const size = this.selectedAttributeService.configuredItem().size;
          assert(size, 'Size was null');
          this.selectedAttributeService.pricingObject.set({ style: mapEntry[0]?.halosku ?? this.configuredItemService.getNewStyleFromSelectedAttributes(this.selectedAttributeService.configuredItem()), size });
        });
      } else {
        const fingerSize = formattedSKUOrStyleNumber.match(/(?<size>\d{2}.?\d{0,2}$)/)?.groups?.['size'];
        const replacerRegex = new RegExp(`${fingerSize ?? null}$`);

        const styleNumber = formattedSKUOrStyleNumber.replace(replacerRegex, '');
        const mapEntry = await this.skuMapService.get(styleNumber);
        if (mapEntry) {
          const fingerSizeNumber = +(fingerSize ?? NaN);
          this.selectedAttributeService.addConfiguredItemToUndoList(null);
          await this.selectedAttributeService.setConfiguredItemFromStyle(mapEntry?.ringBuilderSKU, !isNaN(fingerSizeNumber) ? fingerSizeNumber : undefined, undefined, (b2bData) => {
            b2bData.selectedHaloStyleNumber = styleNumber;
            b2bData.haloStyle = styleNumber;
            const size = this.selectedAttributeService.configuredItem().size;
            assert(size, 'Size was null');
            this.selectedAttributeService.pricingObject.set({ style: styleNumber, size });
          });
        } else {
          throw 'Invalid Size'
        }
      }
      this.navigationService.navigateTo(0);
    } catch (e) {
      this.dialog.open(InvalidSkuDialogComponent);
    } finally {
      this.loadingService.done('topbar:searchSKU');
      this.loadingService.done('selected-attribute:set-item-from-style');
    }
  }

  triggerGAEvent(action: string, title: string, value: string) {
    if (this.googleAnalyticsService.isInitialized) {
      this.googleAnalyticsService.triggerEvent(action, 'top_bar', title, value);
    }
  }
}
